import { Company, Id } from '@nextforce/api'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { useStoredState } from 'shared/hooks/stored_state'
import { useRecentStoresContext } from 'stores/context'
import { useStores } from 'stores/queries/list'

export interface CompaniesContextValue {
  companies: Company[] | undefined
  activeCompany: Company | undefined
  setActiveCompanyId?: (id: Id | undefined) => void
}

const CompaniesContext = createContext({} as CompaniesContextValue)

interface Props {
  children: React.ReactNode
}
export function CompaniesProvider(props: Props) {
  const { data: stores, isLoading, error } = useStores(data => data.stores)
  const { activeStoreId, setActiveStoreId } = useRecentStoresContext()

  const companies = useMemo(() => {
    const uniqueCompanies = stores?.reduce((acc, store) => {
      if (
        store.company &&
        !acc.some(company => company.id === store.company?.id)
      ) {
        acc.push(store.company)
      }
      return acc
    }, [] as Company[])
    return uniqueCompanies || []
  }, [stores])

  const [activeCompanyId, setActiveCompanyId] = useStoredState<Id | undefined>(
    'active_company_id'
  )

  const setActiveCompanyIdWithStore = useCallback(
    (companyId: Id | undefined) => {
      setActiveCompanyId(companyId)
      if (!setActiveStoreId) {
        return
      }

      if (companyId) {
        const companyStores = stores?.filter(
          store => store.company?.id === companyId
        )
        if (companyStores?.length) {
          setActiveStoreId(companyStores[0].id)
        }
      } else {
        setActiveStoreId(undefined)
      }
    },
    [setActiveCompanyId, setActiveStoreId, stores]
  )

  useEffect(() => {
    if (activeStoreId) {
      const activeStoreCompanyId = stores?.find(s => s.id === activeStoreId)
        ?.company?.id
      if (activeStoreCompanyId !== activeCompanyId) {
        setActiveCompanyId(activeStoreCompanyId)
      }
    }
  }, [activeCompanyId, setActiveCompanyId, activeStoreId, stores])

  useEffect(() => {
    if (!companies.length) {
      return
    }

    const hasStoredCompany = !!companies.find(c => c.id === activeCompanyId)
    if (!hasStoredCompany) {
      setActiveCompanyIdWithStore(companies[0].id)
    }
  }, [activeCompanyId, companies, setActiveCompanyIdWithStore])

  const activeCompany = useMemo(() => {
    return companies.find(company => company.id === activeCompanyId)
  }, [companies, activeCompanyId])

  return (
    <StatusBoundary
      isLoading={isLoading}
      loadingMessage={'Loading Companies'}
      error={error}
    >
      <CompaniesContext.Provider
        value={{
          companies,
          setActiveCompanyId: setActiveCompanyIdWithStore,
          activeCompany,
        }}
      >
        {props.children}
      </CompaniesContext.Provider>
    </StatusBoundary>
  )
}

export function useCompaniesContext() {
  return useContext<CompaniesContextValue>(CompaniesContext)
}
